import React, { FC, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Form, Field as RFFField } from 'react-final-form';
import type { FormApi } from 'final-form';
import { Alert } from '@manulife/mux';
import { CreateAgencyFormData, StepProps, SelectOption } from '../types';
import { MUXTextInput, MUXSelect, MUXComboBox, MUXCheckboxGroup } from '../components/FormWrappers';
import { get } from 'lodash';
import { PaymentGatewayType, LINKS } from '@trip-ux/constants';
import { isNonEmptyString, isValidPaymentGateway, isValidWebsiteUrl } from '../validation';
import { useSelector } from 'react-redux';
import { ReduxStore, prepareHeaders } from '@trip-ux/redux-store';
import axios from 'axios';

// --- Types ---

interface Organization {
  code: string;
  organizationName: string;
  category?: string;
  rootOrganizationCode?: string;
}

// --- Constants ---

const STANDALONE_VALUE = '__STANDALONE__';

const BUSINESS_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Agency', value: 'AGENCY' },
  { label: 'Division', value: 'DIVISION' },
  { label: 'Referral', value: 'REFERRAL' }
];

const CATEGORY_OPTIONS: SelectOption[] = [
  { label: 'Agency', value: 'AGENCY' },
  { label: 'Division', value: 'DIVISION' }
];

const SELL_PPP_OPTIONS: SelectOption[] = [
  { label: 'Yes', value: 'TRUE' },
  { label: 'No', value: 'FALSE' }
];

const LOGO_OPTIONS: SelectOption[] = [{ label: 'Has Logo', value: 'HAS_LOGO' }];

const DEFAULT_PAYMENT_GATEWAY_OPTIONS: SelectOption[] = [
  { label: 'Moneris', value: PaymentGatewayType.MONERIS },
  { label: 'Organization', value: PaymentGatewayType.ORGANIZATION }
];

const CODE_EXISTS_ERROR = 'This organization code already exists';

// --- Pure Helper Functions ---

const buildInitialValues = (formData: CreateAgencyFormData): Partial<CreateAgencyFormData> => {
  const derivedSellPpp = formData.sellPPP
    || (formData.programCode === 'MARVEL' ? 'TRUE' : '')
    || (formData.programCode === 'MARVEL_NOPPP' ? 'FALSE' : '');

  return {
    code: formData.code || '',
    organizationName: formData.organizationName || '',
    organizationNameInFrench: formData.organizationNameInFrench || '',
    businessType: formData.businessType || 'AGENCY',
    status: formData.status || 'ACTIVE',
    category: formData.category || formData.businessType || 'AGENCY',
    companyWebsite: formData.companyWebsite || '',
    sellPPP: derivedSellPpp,
    programCode: derivedSellPpp === 'TRUE' ? 'MARVEL' : 'MARVEL_NOPPP',
    BDM: formData.BDM || '',
    rootOrganizationCode: formData.rootOrganizationCode || '',
    parentOrganizationCode: formData.parentOrganizationCode || '',
    affiliationCode: formData.affiliationCode || '',
    paymentGateway: Array.isArray(formData.paymentGateway) ? [...formData.paymentGateway] : [],
    hasLogo: formData.logoFile ? ['HAS_LOGO'] : []
  };
};

const validateStep1 = (
  values: CreateAgencyFormData,
  statusOptions: SelectOption[]
): Record<string, string> => {
  const newErrors: Record<string, string> = {};
  const allowedBusinessTypes = BUSINESS_TYPE_OPTIONS.map(o => o.value);
  const allowedCategories = CATEGORY_OPTIONS.map(o => o.value);
  const allowedStatuses = statusOptions.map(o => o.value);

  if (!isNonEmptyString(values.code)) newErrors.code = 'Organization code is required';
  if (!isNonEmptyString(values.organizationName)) newErrors.organizationName = 'Organization name is required';

  validateEnumField(values.businessType, allowedBusinessTypes, 'businessType', 'Business type', newErrors);
  validateEnumField(values.category, allowedCategories, 'category', 'Category', newErrors);

  if (!isNonEmptyString(values.status)) {
    newErrors.status = 'Status is required';
  } else if (allowedStatuses.length > 0 && !allowedStatuses.includes(String(values.status))) {
    newErrors.status = 'Invalid status';
  }

  if (isNonEmptyString(values.companyWebsite) && !isValidWebsiteUrl(values.companyWebsite)) {
    newErrors.companyWebsite = 'Invalid website URL';
  }
  if (!['TRUE', 'FALSE'].includes(String(values.sellPPP || ''))) {
    newErrors.sellPPP = 'Select whether the agency can sell PPP';
  }
  if (!isNonEmptyString(values.rootOrganizationCode) && values.businessType !== 'DIVISION') {
    newErrors.rootOrganizationCode = 'Root organization code is required';
  }
  if (!isNonEmptyString(values.affiliationCode)) newErrors.affiliationCode = 'Affiliation code is required';

  validatePaymentGateways(values.paymentGateway, newErrors);
  return newErrors;
};

const validateEnumField = (
  value: string | undefined,
  allowed: string[],
  field: string,
  label: string,
  errors: Record<string, string>
): void => {
  if (!isNonEmptyString(value)) {
    errors[field] = `${label} is required`;
  } else if (!allowed.includes(String(value))) {
    errors[field] = `Invalid ${label.toLowerCase()}`;
  }
};

const validatePaymentGateways = (
  gateways: string[] | undefined,
  errors: Record<string, string>
): void => {
  const selected = Array.isArray(gateways) ? gateways : [];
  if (selected.length === 0) {
    errors.paymentGateway = 'Select at least one payment gateway';
  } else if (!selected.every(g => isValidPaymentGateway(g))) {
    errors.paymentGateway = 'Invalid payment gateway selection';
  }
};

const deriveRootCode = (
  values: CreateAgencyFormData,
  finalParentCode: string,
  organizations: Organization[]
): string => {
  const isDivision = values.businessType === 'DIVISION';
  const isStandalone = values.rootOrganizationCode === STANDALONE_VALUE;

  if (isDivision && finalParentCode) {
    const parentOrg = organizations.find(org => org.code === finalParentCode);
    return parentOrg?.rootOrganizationCode || finalParentCode;
  }
  if (isDivision || isStandalone) {
    return values.code || '';
  }
  // AGENCY non-standalone
  if (finalParentCode) {
    const parentOrg = organizations.find(org => org.code === finalParentCode);
    return parentOrg?.rootOrganizationCode || values.rootOrganizationCode || '';
  }
  return values.rootOrganizationCode || '';
};

const buildRootOrgOptions = (organizations: Organization[]): SelectOption[] => {
  const standaloneOption = { label: 'Create as Standalone Organization', value: STANDALONE_VALUE };
  const rootOrgs = organizations
    .filter(org => org.rootOrganizationCode && org.code === org.rootOrganizationCode)
    .map(org => ({ label: `${org.code} - ${org.organizationName}`, value: org.code }));
  return [standaloneOption, ...rootOrgs];
};

const buildDivisionParentOptions = (organizations: Organization[]): SelectOption[] => {
  const rootCodes = new Set(
    organizations
      .filter(org => org.rootOrganizationCode && org.code === org.rootOrganizationCode)
      .map(org => org.code)
  );
  const validParents = organizations
    .filter(org => rootCodes.has(org.code) || (org.rootOrganizationCode && rootCodes.has(org.rootOrganizationCode)))
    .map(org => ({ label: `${org.code} - ${org.organizationName}`, value: org.code }));
  return [{ label: 'None (Standalone Division)', value: '' }, ...validParents];
};

const buildAgencyParentOptions = (organizations: Organization[], selectedRoot: string): SelectOption[] => {
  if (!selectedRoot || selectedRoot === STANDALONE_VALUE) {
    return [];
  }
  const divisionOrgs = organizations
    .filter(org => org.category === 'DIVISION' && (org.rootOrganizationCode === selectedRoot || org.code === selectedRoot))
    .map(org => ({ label: `${org.code} - ${org.organizationName}`, value: org.code }));
  return [{ label: 'None (Direct child of root)', value: '' }, ...divisionOrgs];
};

const getParentPlaceholder = (businessType: string | undefined, selectedRoot: string): string => {
  if (businessType === 'DIVISION') {
    return 'Select parent or leave empty for standalone';
  }
  return selectedRoot === STANDALONE_VALUE ? 'N/A for standalone' : 'Search or select parent';
};

// --- Custom Hook: Code Existence Check ---

const useCodeExistsCheck = (
  contextPath: string,
  state: ReduxStore,
  organizations: Organization[]
) => {
  const [codeExists, setCodeExists] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const markCodeExists = useCallback(() => {
    setCodeExists(true);
    setErrors(prev => ({ ...prev, code: CODE_EXISTS_ERROR }));
  }, []);

  const clearCodeError = useCallback(() => {
    setCodeExists(false);
    setErrors(prev => {
      const { code: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const checkCodeExists = useCallback(async (code: string) => {
    if (!code?.trim()) {
      clearCodeError();
      return;
    }

    const normalizedCode = code.trim().toUpperCase();
    if (organizations.some(org => org.code.toUpperCase() === normalizedCode)) {
      markCodeExists();
      return;
    }

    try {
      const response = await axios.get(
        `${contextPath}${LINKS.SEARCH_ORGANIZATION}`,
        {
          headers: prepareHeaders(state) as Record<string, string>,
          params: { organizationCode: normalizedCode },
        }
      );
      if (response.data?.code) {
        markCodeExists();
      } else {
        clearCodeError();
      }
    } catch (error) {
      console.error('Error checking organization code:', error);
      clearCodeError();
    }
  }, [contextPath, state, organizations, markCodeExists, clearCodeError]);

  return { codeExists, errors, setErrors, checkCodeExists };
};

// --- Sub-Components ---

interface BasicInfoFieldsProps {
  statusOptions: SelectOption[];
  rootOrganizationOptions: SelectOption[];
  parentOrganizationOptions: SelectOption[];
  paymentGatewayOptions: SelectOption[];
  businessType: string | undefined;
  selectedRoot: string;
  codeExists: boolean;
  checkCodeExists: (code: string) => void;
  onBusinessTypeChange: (value: string) => void;
  onRootOrgChange: (value: string) => void;
  onSellPppChange: (value: string) => void;
  onPaymentGatewayChange: (values: string[]) => void;
}

const BasicInfoFields: FC<BasicInfoFieldsProps> = ({
  statusOptions, rootOrganizationOptions, parentOrganizationOptions,
  paymentGatewayOptions, businessType, selectedRoot, codeExists,
  checkCodeExists, onBusinessTypeChange, onRootOrgChange, onSellPppChange,
  onPaymentGatewayChange
}) => (
  <>
    <div className="mux-grid mux-grid-cols-1 tablet:mux-grid-cols-2 mux-gap-4">
      <div className="mux-col-span-1">
        <RFFField name="code" component={MUXTextInput} label="Organization Code" type="text" required
          onInputBlur={checkCodeExists} asyncError={codeExists ? CODE_EXISTS_ERROR : undefined} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="organizationName" component={MUXTextInput} label="Organization Name" type="text" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="organizationNameInFrench" component={MUXTextInput} label="Organization Name (French)" type="text" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="status" component={MUXSelect} label="Status" options={statusOptions} required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="businessType" component={MUXSelect} label="Business Type" options={BUSINESS_TYPE_OPTIONS}
          onValueChange={onBusinessTypeChange} required />
      </div>
      {businessType !== 'DIVISION' && (
        <div className="mux-col-span-1">
          <RFFField name="rootOrganizationCode" component={MUXComboBox} label="Root Organization"
            options={rootOrganizationOptions} placeholder="Search or select root organization"
            onValueChange={onRootOrgChange} required />
        </div>
      )}
      <div className="mux-col-span-1">
        <RFFField name="parentOrganizationCode" component={MUXComboBox} label="Parent Organization"
          options={parentOrganizationOptions} placeholder={getParentPlaceholder(businessType, selectedRoot)}
          disabled={businessType !== 'DIVISION' && (!selectedRoot || selectedRoot === STANDALONE_VALUE)} />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="affiliationCode" component={MUXTextInput} label="Affiliation Code" type="text" required
          tooltip="Must match an existing Affiliation organization code." />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="BDM" component={MUXTextInput} label="BDM (Business Development Manager)" type="text" required />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="companyWebsite" component={MUXTextInput} label="Company Website" type="url" />
      </div>
      <div className="mux-col-span-1">
        <RFFField name="sellPPP" component={MUXSelect} label="Sell Premium Protection Plans (PPP)?"
          options={SELL_PPP_OPTIONS} onValueChange={onSellPppChange} required />
      </div>
    </div>

    <div className="form-section">
      <h3>Payment Gateway</h3>
      <RFFField name="paymentGateway" component={MUXCheckboxGroup} label="Select Payment Gateway Options"
        options={paymentGatewayOptions} onChange={onPaymentGatewayChange} />
    </div>

    <div className="form-section">
      <h3>Branding</h3>
      <RFFField name="hasLogo" component={MUXCheckboxGroup} label="Logo Options" options={LOGO_OPTIONS} />
    </div>
  </>
);

// --- Main Component ---

const Step1BasicInfo: FC<StepProps> = ({ formData, onNext, labels, formRef }) => {
  const formInstanceRef = useRef<FormApi<CreateAgencyFormData> | null>(null);
  const initialValuesRef = useRef<Partial<CreateAgencyFormData> | null>(null);
  const [selectedRoot, setSelectedRoot] = useState<string>(formData.rootOrganizationCode || '');
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>(formData.businessType || 'AGENCY');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);

  const state = useSelector((store: ReduxStore) => store);
  const contextPath = get(state, 'config.options.contextPath', '');

  const { codeExists, errors, setErrors, checkCodeExists } = useCodeExistsCheck(contextPath, state, organizations);

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        const response = await axios.get(
          `${contextPath}${LINKS.AGENCY_CREATE_ORGANIZATIONS}`,
          { headers: prepareHeaders(state) as Record<string, string> }
        );
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      } finally {
        setIsLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, [contextPath]);

  const rootOrganizationOptions = useMemo(() => buildRootOrgOptions(organizations), [organizations]);

  const parentOrganizationOptions = useMemo(() => {
    if (selectedBusinessType === 'DIVISION') {
      return buildDivisionParentOptions(organizations);
    }
    return buildAgencyParentOptions(organizations, selectedRoot);
  }, [organizations, selectedRoot, selectedBusinessType]);

  const statusOptions: SelectOption[] = get(labels, 'statusOptions', []);
  const configuredGateways = get(labels, 'paymentGatewayOptions', []);
  const paymentGatewayOptions = Array.isArray(configuredGateways) && configuredGateways.length > 0
    ? configuredGateways
    : DEFAULT_PAYMENT_GATEWAY_OPTIONS;

  const handleAlertClose = useCallback(() => setErrors({}), [setErrors]);

  const handleBusinessTypeChange = useCallback((selected: string) => {
    formInstanceRef.current?.change('category', selected);
    setSelectedBusinessType(selected);
    formInstanceRef.current?.change('rootOrganizationCode', '');
    formInstanceRef.current?.change('parentOrganizationCode', '');
    setSelectedRoot('');
  }, []);

  const handleRootOrgChange = useCallback((selected: string) => {
    setSelectedRoot(selected);
    formInstanceRef.current?.change('parentOrganizationCode', '');
  }, []);

  const handleSellPppChange = useCallback((selected: string) => {
    formInstanceRef.current?.change('programCode', selected === 'TRUE' ? 'MARVEL' : 'MARVEL_NOPPP');
  }, []);

  const handlePaymentGatewayChange = useCallback((selectedValues: string[]) => {
    if (selectedValues.includes(PaymentGatewayType.ORGANIZATION)) {
      formInstanceRef.current?.change('paymentGateway',
        Array.from(new Set([PaymentGatewayType.ORGANIZATION, PaymentGatewayType.MONERIS])));
    } else {
      formInstanceRef.current?.change('paymentGateway', selectedValues);
    }
  }, []);

  if (!initialValuesRef.current) {
    initialValuesRef.current = buildInitialValues(formData);
  }

  const validate = useCallback(
    (values: CreateAgencyFormData) => validateStep1(values, statusOptions),
    [statusOptions]
  );

  const handleSubmit = (values: CreateAgencyFormData) => {
    const newErrors = validateStep1(values, statusOptions);
    if (codeExists) newErrors.code = CODE_EXISTS_ERROR;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const isStandalone = values.rootOrganizationCode === STANDALONE_VALUE;
    const finalParentCode = isStandalone ? '' : (values.parentOrganizationCode || '');

    const normalizedCode = (values.code || '').toUpperCase().trim();
    const orgNameFr = isNonEmptyString(values.organizationNameInFrench)
      ? values.organizationNameInFrench
      : values.organizationName;

    onNext({
      ...values,
      code: normalizedCode,
      organizationNameInFrench: orgNameFr,
      rootOrganizationCode: deriveRootCode(values, finalParentCode, organizations),
      parentOrganizationCode: finalParentCode || undefined,
      affiliationCode: values.affiliationCode?.trim(),
      category: values.businessType === 'REFERRAL' ? 'AGENCY' : values.businessType,
      businessType: values.businessType,
      programCode: values.sellPPP === 'TRUE' ? 'MARVEL' : 'MARVEL_NOPPP',
      paymentGateway: values.paymentGateway || [],
      logoFile: Array.isArray(values.hasLogo) && values.hasLogo.includes('HAS_LOGO') ? 'HAS_LOGO' : ''
    });
  };

  useEffect(() => {
    if (formRef && formInstanceRef.current) {
      formRef({ submit: () => formInstanceRef.current?.submit() });
    }
  }, [formRef]);

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      initialValues={initialValuesRef.current}
      render={({ handleSubmit: formSubmit, form, values }) => {
        formInstanceRef.current = form;
        return (
          <form onSubmit={formSubmit} className="step-form">

            {Object.keys(errors).length > 0 && (
              <Alert severity="alert" onClose={handleAlertClose}>
                Please fix the errors below before proceeding.
              </Alert>
            )}

            <BasicInfoFields
              statusOptions={statusOptions}
              rootOrganizationOptions={rootOrganizationOptions}
              parentOrganizationOptions={parentOrganizationOptions}
              paymentGatewayOptions={paymentGatewayOptions}
              businessType={values.businessType}
              selectedRoot={selectedRoot}
              codeExists={codeExists}
              checkCodeExists={checkCodeExists}
              onBusinessTypeChange={handleBusinessTypeChange}
              onRootOrgChange={handleRootOrgChange}
              onSellPppChange={handleSellPppChange}
              onPaymentGatewayChange={handlePaymentGatewayChange}
            />
          </form>
        );
      }}
    />
  );
};

export default Step1BasicInfo;
